import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

export function apiError(detail, fallback = "Something went wrong. Please try again.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function StudentAuthProvider({ children }) {
  const [student, setStudent] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/student/me`, { withCredentials: true });
      setStudent(data);
      return data;
    } catch {
      setStudent(false);
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, remember = true) => {
    const { data } = await axios.post(
      `${API}/student/login`,
      { email, password, remember },
      { withCredentials: true },
    );
    setStudent(data.student);
    return data.student;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/student/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setStudent(false);
  }, []);

  const value = useMemo(
    () => ({ student, checking, refresh, login, logout, setStudent }),
    [student, checking, refresh, login, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStudentAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStudentAuth must be used inside <StudentAuthProvider>");
  return ctx;
}

export const studentApi = axios.create({ baseURL: API, withCredentials: true });
